import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Shield, ArrowUp } from 'lucide-react';
import { companyInfo, openingHours } from '../data';

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();

  const getServiceIdByTitle = (title) => {
    const t = title.toLowerCase();
    if (t.includes("sokkel") || t.includes("fundament")) return "sokkel";
    if (t.includes("terrændæk")) return "terraendaek";
    if (t.includes("belægning")) return "belaegning";
    if (t.includes("støttemur")) return "stoettemur";
    if (t.includes("gravearbejde")) return "gravearbejde";
    if (t.includes("nedbrydning")) return "nedbrydning";
    return null;
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleNavClick = (id) => {
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        const offset = 120;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <footer className="w-full bg-slate-950 text-[#f1f1f1] py-16 px-4 sm:px-6 lg:px-8 border-t border-slate-900 relative">
      
      {/* Scroll to top button */}
      <button
        onClick={scrollToTop}
        className="absolute -top-6 right-8 bg-orange-500 hover:bg-orange-600 text-white p-3 shadow-lg hover:shadow-orange-500/20 transition-all cursor-pointer"
        aria-label="Scroll til toppen"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        
        {/* Column 1: Firmainfo */}
        <div className="flex flex-col text-left">
          <h4 className="text-white font-extrabold uppercase tracking-wider text-xs mb-6">
            Firmainfo
          </h4>
          <div className="space-y-4 text-sm text-[#f1f1f1]/90">
            <p className="font-bold text-white uppercase tracking-wider text-sm">{companyInfo.name}</p>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
              <span>{companyInfo.address}</span>
            </div>
            <a
              href={`tel:${companyInfo.phoneFormatted}`}
              className="flex items-center gap-3 hover:text-orange-400 transition-colors"
            >
              <Phone className="w-4 h-4 text-orange-500 shrink-0" />
              <span>Tlf: {companyInfo.phone}</span>
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-3 hover:text-orange-400 transition-colors"
            >
              <Mail className="w-4 h-4 text-orange-500 shrink-0" />
              <span>{companyInfo.email}</span>
            </a>
            <div className="flex items-center gap-3">
              <Shield className="w-4 h-4 text-orange-500 shrink-0 animate-pulse" />
              <span>CVR: {companyInfo.cvr}</span>
            </div>
          </div>
        </div>

        {/* Column 2: Vi Udfører */}
        <div className="flex flex-col text-left">
          <h4 className="text-white font-extrabold uppercase tracking-wider text-xs mb-6">
            Vi udfører
          </h4>
          <ul className="space-y-2.5 text-xs text-[#f1f1f1]/90 uppercase tracking-wider font-medium">
            {companyInfo.servicesList.map((service, index) => {
              const serviceId = getServiceIdByTitle(service);
              return (
                <li key={index}>
                  {serviceId ? (
                    <Link
                      to={`/ydelser/${serviceId}`}
                      className="hover:text-orange-400 transition-colors text-left cursor-pointer block"
                    >
                      {service}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleNavClick('ydelser')}
                      className="hover:text-orange-400 transition-colors text-left cursor-pointer block w-full"
                    >
                      {service}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Column 3: Kan træffes på telefonen */}
        <div className="flex flex-col text-left">
          <h4 className="text-white font-extrabold uppercase tracking-wider text-xs mb-6">
            Kan træffes på tlf.
          </h4>
          <div className="space-y-1.5 text-sm">
            {openingHours.map((item, index) => (
              <div key={index} className="flex justify-between border-b border-slate-800/50 pb-1">
                <span className="font-medium text-[#f1f1f1]/90">{item.day}:</span>
                <span className="text-[#f1f1f1]/70">{item.hours}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Column 4: Brand CTA & Quality Assurance */}
        <div className="flex flex-col text-left justify-between">
          <div>
            <button
              onClick={scrollToTop}
              className="flex items-center hover:opacity-90 transition-opacity cursor-pointer mb-3"
            >
              <img 
                src="https://assets.auxodev.dk/assets/154be17d-5a98-49b2-8816-0581c018ef0c/6730a9df-79da-4717-9885-5db4c64f6acc.png" 
                alt="Peter Sloth Logo" 
                className="h-12 w-auto object-contain"
              />
            </button>
            <p className="text-xs italic text-[#f1f1f1]/50 mt-1">
              {companyInfo.tagline}
            </p>
          </div>
          
          <div className="mt-6 space-y-4">
            <Link
              to="/kontakt"
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase text-[10px] tracking-wider py-2.5 px-4 transition-all duration-200 rounded-none text-center cursor-pointer"
            >
              Kontakt
            </Link>
          </div>
        </div>

      </div>

      {/* Copyright Bar */}
      <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-slate-900/50 text-center text-xs text-[#f1f1f1]/40 flex flex-col sm:flex-row justify-between items-center gap-4">
        <p>Copyright © {new Date().getFullYear()} - Peter Sloth, CVR {companyInfo.cvr} - Alle rettigheder forbeholdes.</p>
        <div className="flex gap-4 tracking-wider font-semibold text-[10px]">
          <Link to="/om-os" className="hover:text-[#f1f1f1]/80 transition-colors cursor-pointer uppercase">Om os</Link>
          <span>·</span>
          <Link to="/galleri" className="hover:text-[#f1f1f1]/80 transition-colors cursor-pointer uppercase">Galleri</Link>
          <span>·</span>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-[#f1f1f1]/80 transition-colors cursor-pointer uppercase">Cookies</button>
          <span>·</span>
          <button onClick={() => handleNavClick('om')} className="hover:text-[#f1f1f1]/80 transition-colors cursor-pointer uppercase">Privatlivspolitik</button>
        </div>
      </div>

    </footer>
  );
}
